// src/pages/TripPlanner/components/EnhancedTripForm.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  Typography, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Chip,
  Grid,
  Slider,
  FormHelperText,
  Autocomplete,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { 
  Celebration as CelebrationIcon,
  Business as BusinessIcon,
  FamilyRestroom as FamilyIcon,
  Groups as FriendsIcon,
  Hiking as AdventureIcon,
  BeachAccess as RelaxIcon,
  Restaurant as FoodIcon,
  Museum as CultureIcon,
  AddCircle as AddIcon
} from '@mui/icons-material';

// Trip purpose options
const tripPurposes = [
  { value: 'leisure', label: 'Leisure & Relaxation', icon: <RelaxIcon /> },
  { value: 'adventure', label: 'Adventure & Exploration', icon: <AdventureIcon /> },
  { value: 'culture', label: 'Cultural Experience', icon: <CultureIcon /> },
  { value: 'food', label: 'Food & Culinary', icon: <FoodIcon /> },
  { value: 'celebration', label: 'Celebration', icon: <CelebrationIcon /> },
  { value: 'business', label: 'Business', icon: <BusinessIcon /> },
  { value: 'family', label: 'Family Visit', icon: <FamilyIcon /> }
];

// Trip style options
const tripStyles = [
  { value: 'luxury', label: 'Luxury' },
  { value: 'comfort', label: 'Comfortable' },
  { value: 'budget', label: 'Budget-friendly' },
  { value: 'backpacking', label: 'Backpacking' },
  { value: 'glamping', label: 'Glamping' }
];

// Travel pace options
const travelPaces = [
  { value: 'relaxed', label: 'Relaxed & Slow' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'busy', label: 'Busy & Fast-paced' }
];

// Common interests options
const interestOptions = [
  'Museums', 'Hiking', 'Beaches', 'Food Tours', 'Shopping', 
  'Historical Sites', 'Nightlife', 'Photography', 'Wildlife',
  'Art Galleries', 'Local Markets', 'Concerts', 'Festivals',
  'Architecture', 'Theme Parks', 'Biking', 'Water Sports',
  'Wine Tasting', 'Yoga', 'Spa & Wellness'
];

// Group relationship options
const groupRelationships = [
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family with Kids' },
  { value: 'friends', label: 'Close Friends', icon: <FriendsIcon /> },
  { value: 'colleagues', label: 'Work Colleagues' },
  { value: 'solo', label: 'Solo Traveler' },
  { value: 'group', label: 'Mixed Group' }
];

interface EnhancedTripFormProps {
  onSubmit: (tripData: any) => void;
  initialData?: any;
  existingTrips?: any[];
}

const EnhancedTripForm: React.FC<EnhancedTripFormProps> = ({ 
  onSubmit, 
  initialData = {}, 
  existingTrips = [] 
}) => {
  const [tripData, setTripData] = useState({
    title: initialData.title || '',
    description: initialData.description || '',
    startDate: initialData.startDate || null,
    endDate: initialData.endDate || null,
    destination: initialData.destination || '',
    purpose: initialData.purpose || '',
    style: initialData.style || 'comfort',
    pace: initialData.pace || 'moderate',
    budget: initialData.budget || [100, 500],
    groupSize: initialData.groupSize || 2,
    groupRelationship: initialData.groupRelationship || '',
    specialOccasion: initialData.specialOccasion || '',
    interests: initialData.interests || [],
    dietaryRestrictions: initialData.dietaryRestrictions || [],
    accessibilityNeeds: initialData.accessibilityNeeds || '',
    additionalNotes: initialData.additionalNotes || '',
    idealTemperature: initialData.idealTemperature || 'warm'
  });

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setTripData({
      ...tripData,
      [name]: value
    });
  };

  const handleDateChange = (name: string, date: Date | null) => {
    setTripData({
      ...tripData,
      [name]: date
    });
  };

  const handleBudgetChange = (_event: Event, newValue: number | number[]) => {
    setTripData({
      ...tripData,
      budget: newValue as number[]
    });
  };

  const handleInterestsChange = (_event: any, newValue: string[]) => {
    setTripData({
      ...tripData,
      interests: newValue
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(tripData);
  };

  const copyExistingTrip = (trip: any) => {
    setTripData({
      ...tripData,
      ...trip
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h5" gutterBottom>
            Plan Your Perfect Trip
          </Typography>
          
          {existingTrips && existingTrips.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Your Existing Trips
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {existingTrips.map((trip) => (
                  <Chip 
                    key={trip.id} 
                    label={trip.title} 
                    onClick={() => copyExistingTrip(trip)} 
                    clickable 
                  />
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}
          
          <Grid container spacing={3}>
            {/* Basic Trip Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Basic Trip Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Trip Title"
                name="title"
                value={tripData.title}
                onChange={handleChange}
                placeholder="Summer in Paris"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Destination"
                name="destination"
                value={tripData.destination}
                onChange={handleChange}
                placeholder="Paris, France"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Start Date"
                value={tripData.startDate}
                onChange={(date) => handleDateChange('startDate', date as Date | null)}
                renderInput={params => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <DatePicker
                label="End Date"
                value={tripData.endDate}
                onChange={(date) => handleDateChange('endDate', date as Date | null)}
                renderInput={params => (
                  <TextField {...params} size='small' fullWidth />
                )}
                minDate={tripData.startDate || undefined}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Trip Description"
                name="description"
                value={tripData.description}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="What are you looking forward to on this trip?"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {/* Trip Style & Purpose */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Trip Style & Purpose
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Trip Purpose</InputLabel>
                <Select
                  name="purpose"
                  value={tripData.purpose}
                  onChange={handleChange}
                  label="Trip Purpose"
                >
                  {tripPurposes.map(purpose => (
                    <MenuItem key={purpose.value} value={purpose.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {purpose.icon && <Box sx={{ mr: 1 }}>{purpose.icon}</Box>}
                        {purpose.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Travel Style</InputLabel>
                <Select
                  name="style"
                  value={tripData.style}
                  onChange={handleChange}
                  label="Travel Style"
                >
                  {tripStyles.map(style => (
                    <MenuItem key={style.value} value={style.value}>
                      {style.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Travel Pace</InputLabel>
                <Select
                  name="pace"
                  value={tripData.pace}
                  onChange={handleChange}
                  label="Travel Pace"
                >
                  {travelPaces.map(pace => (
                    <MenuItem key={pace.value} value={pace.value}>
                      {pace.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Group Relationship</InputLabel>
                <Select
                  name="groupRelationship"
                  value={tripData.groupRelationship}
                  onChange={handleChange}
                  label="Group Relationship"
                >
                  {groupRelationships.map(relation => (
                    <MenuItem key={relation.value} value={relation.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {relation.icon && <Box sx={{ mr: 1 }}>{relation.icon}</Box>}
                        {relation.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Special Occasion"
                name="specialOccasion"
                value={tripData.specialOccasion}
                onChange={handleChange}
                placeholder="Birthday, Anniversary, etc."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Group Size</InputLabel>
                <Select
                  name="groupSize"
                  value={tripData.groupSize}
                  onChange={handleChange}
                  label="Group Size"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '10+'].map(size => (
                    <MenuItem key={size} value={size}>
                      {size} {size === 1 ? 'Person' : 'People'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography gutterBottom>Daily Budget Range (per person)</Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={tripData.budget}
                  onChange={handleBudgetChange}
                  valueLabelDisplay="on"
                  min={0}
                  max={1000}
                  step={50}
                  marks={[
                    { value: 0, label: '$0' },
                    { value: 250, label: '$250' },
                    { value: 500, label: '$500' },
                    { value: 750, label: '$750' },
                    { value: 1000, label: '$1000+' }
                  ]}
                  valueLabelFormat={(value) => `$${value}`}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            {/* Preferences & Requirements */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Interests & Preferences
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={interestOptions}
                value={tripData.interests}
                onChange={handleInterestsChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Interests & Activities"
                    placeholder="Select interests"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                freeSolo
              />
              <FormHelperText>
                Select activities and interests that your group enjoys
              </FormHelperText>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dietary Restrictions"
                name="dietaryRestrictions"
                value={tripData.dietaryRestrictions}
                onChange={handleChange}
                placeholder="Vegetarian, Gluten-free, etc."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Accessibility Needs"
                name="accessibilityNeeds"
                value={tripData.accessibilityNeeds}
                onChange={handleChange}
                placeholder="Wheelchair access, etc."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ideal Weather</InputLabel>
                <Select
                  name="idealTemperature"
                  value={tripData.idealTemperature}
                  onChange={handleChange}
                  label="Ideal Weather"
                >
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="mild">Mild</MenuItem>
                  <MenuItem value="warm">Warm</MenuItem>
                  <MenuItem value="hot">Hot</MenuItem>
                  <MenuItem value="any">Any</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="additionalNotes"
                value={tripData.additionalNotes}
                onChange={handleChange}
                multiline
                rows={3}
                placeholder="Any other details that would help plan your perfect trip..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  startIcon={<AddIcon />}
                >
                  {initialData.id ? 'Update Trip' : 'Create Trip'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </LocalizationProvider>
  );
};

export default EnhancedTripForm;